import { ApolloError } from "@apollo/client";
import { DataDumpRow } from "types/types";
// TODO: Figure out how to use the Asrk schema

export type ReportParams = {
  urlDate: string;
  id: string;
  shift: string;
  ckey: string;
  processGroupName: string;
  machineName: string;
  machine: string;
  year: string;
  week: string;
};

export interface Process {
  id: string;
  ckey: string;
  name: string;
  callStart?: number | null;
  showCountNotMetricOnGroupTile: boolean;
  outputThisShift?: number | null;
  outputThisShiftThisSku?: number | null;
}

export interface Taps {
  id: string;
  name: string;
  machemetryId: string;
}
export interface ProcessWithTaps {
  id: string;
  taps: Taps[];
}

export interface ProcessGroup {
  count?: number;
  groupId: string;
  summaryGroup: string;
  processes: Process[];
  longName: string;
  shortName: string;
  output: number | null;
  outputLabel: string;
  showCountNotMetricOnGroupTile: boolean;
  isIdleUntagged: boolean;
  ckey: string;
  callStart?: number | null;
}

export interface ProcessDetails {
  processId?: string;
  name?: string;
  status?: string;
  statusStart?: number | null;
  outputLabel?: string;
  showOutput?: boolean;
  showCountNotMetricOnTile: boolean;
  callStart?: number | null;
  messageTime?: number | null;
  callTarget?: string | null;
  callTargetIcon?: string | null;
  skuCode?: string;
  skuName?: string | null;
  skuDescription?: string | null;
  skuStart?: number | null;
  operatorName?: string | null;
  operatorAvatar?: string | null;
  outputThisShift?: string | null;
  outputThisShiftThisSku?: string | null;
  recentTicks?: number[] | null;
  ticksThisShift?: number | null;
  ticksThisShiftThisSku?: number | null;
  isSelected: boolean;
  opSku?: string | null;
  teamSize?: number | null;
  ckey: string | null;
  geForQuery?: number | null;
  recentTicksGe?: number | null;
  recentTicksWindow?: number | null;
}
export interface TimeRange {
  ge: number | null;
  lt: number | null;
}

// Not Null (NN) variant for certain use cases
export interface TimeRangeNN {
  ge: number;
  lt: number;
}
export interface TimeRangeFinite {
  ge: number | string;
  lt: number | string;
}

export type DateBasedTimeRangeFinite = {
  ge: Date;
  lt: Date;
};

export interface EventInterface {
  t: number;
}

export interface OutputEventInterface extends EventInterface {
  output: Output;
}

export interface Segment {
  range: TimeRange;
  fullRange?: TimeRange;
  events?: [EventInterface];
}

export type useQueryResults = {
  data: { dataDump: DataDumpType[] | null };

  error?: ApolloError;
  loading: boolean;
};

export type ManyProductionSegmentsArrays = {
  [processId: string]: ProductionSegment[];
};

export type OutputCountAndOeeObj = {
  processIds: string[];
  range: TimeRangeFinite;
  outputCount: number | null;
  oeePercent: number | null;
  effectiveUptimePercent: number | null; // OPEQ
  productionTime: number | null;
};

export type Rate = number; // TODO: Make this something that also has the units
export interface RateSegment extends Segment {
  rate: Rate;
}

export interface Output {
  good?: number;
  bad?: number;
  total?: number;
}
export interface OutputSegment extends Segment {
  output: Output;
}

export type Tag = {
  name?: string;
  tagPath?: [string];
};
export interface TagSegment extends Segment {
  tag: Tag;
}

export type Pnl = {
  category: string;
  // category: PnlCategory;
};

export interface ProductionSegment extends Segment {
  rateSegments?: [RateSegment];
  outputSegments?: [OutputSegment];
  output?: Output;
  pnl: Pnl;
  tag?: Tag;
  isSameCategoryAsPrevSegment?: boolean;
}

export type TagSummary = {
  tag: Tag;
  totalDurationMs: number;
};
type LossCategorySummary = {
  tags: TagSummary[];
  totalDurationMs: number;
};
type ProducingSummary = {
  tags: TagSummary[];
  totalDurationMs: number;
};
export type ProductionAggregate = {
  asOf: number;
  notScheduled: LossCategorySummary;
  planned: LossCategorySummary;
  producing: ProducingSummary;
  range: TimeRange;
  totalDurationMs: number;
  uncollected: LossCategorySummary;
  unknown: LossCategorySummary;
  unplanned: LossCategorySummary;
  waiting: LossCategorySummary;
  shift?: Shift;
};

export type Shift = {
  name?: string;
};

// TODO: not sure how to override an extended interface (Segment -> override range)
export interface ShiftSegment {
  shift: Shift;
  range: TimeRangeFinite;
  fullRange?: TimeRange;
  events?: [EventInterface];
}

export type SimplifiedShiftSegment = {
  name?: string;
  ge: number;
  lt: number;
};

export type Sku = {
  code?: string;
  name?: string;
};

export enum PnlCategoriesEnum {
  producing = "producing",
  planned = "planned",
  unplanned = "unplanned",
  waiting = "waiting",
  unknown = "unknown",
  notScheduled = "notScheduled",
  uncollected = "uncollected",
}

export enum PnlCategoryLabelsEnum {
  producing = "Producing",
  planned = "Planned",
  unplanned = "Unplanned",
  waiting = "Waiting",
  unknown = "Unknown",
  notScheduled = "Not Scheduled",
  uncollected = "Uncollected",
}

export enum ProducingLossesEnum {
  effectiveProducingTime = "effectiveProducingTime",
  qualityLoss = "qualityLoss",
  slowdownLoss = "slowdownLoss",
  minorStopLoss = "minorStopLoss",
}

export enum ProducingLossesLabelsEnum {
  effectiveProducingTime = "Effective Producing Time",
  qualityLoss = "Quality Loss + WIP Increase",
  slowdownLoss = "Slowdown Loss",
  minorStopLoss = "Minor Stop Loss",
}

export const PnlCategoriesWithTags = [
  PnlCategoriesEnum.planned,
  PnlCategoriesEnum.waiting,
  PnlCategoriesEnum.unplanned,
  PnlCategoriesEnum.notScheduled,
];

export const PnlCategoriesWithTagsAndProducingTags = [
  ...PnlCategoriesWithTags,
  PnlCategoriesEnum.producing,
];

export const PRODUCTION_PNL_CATEGORIES = [
  PnlCategoriesEnum.producing,
  PnlCategoriesEnum.planned,
  PnlCategoriesEnum.waiting,
  PnlCategoriesEnum.unplanned,
  PnlCategoriesEnum.unknown,
];

export const DOWNTIME_PNL_CATEGORIES = [
  PnlCategoriesEnum.planned,
  PnlCategoriesEnum.waiting,
  PnlCategoriesEnum.unplanned,
  PnlCategoriesEnum.unknown,
];

export interface RollupCategories {
  producing: number;
  planned: number;
  unplanned: number;
  waiting: number;
  unknown: number;
  notScheduled: number;
  uncollected: number;
  shift?: Shift | null;
}

export type PnlsWithMeasurementObj = {
  [Category in keyof PnlCategoriesEnum]: number;
};
export type TagsWithMeasurementObj = {
  [tagName: string]: number;
};

export type PnlsOrTagsWithMeasurementObj =
  | PnlsWithMeasurementObj
  | TagsWithMeasurementObj;

export type PnlCategoryObjOfObjectsOfNumbers = {
  [Category in keyof typeof PnlCategoriesEnum]: PnlsOrTagsWithMeasurementObj;
};

export type PnlCategoryObjOfTagTimes = {
  [Category in keyof typeof PnlCategoriesEnum]: TagsWithMeasurementObj;
};

export interface RollupWithTagsAndCounts extends RollupCategories {
  tags: PnlCategoryObjOfTagTimes;
  counts: PnlCategoryObjOfTagTimes;
}

export interface RollupWithTagsAndCountsAndRange
  extends RollupWithTagsAndCounts {
  range: TimeRangeFinite;
  totalDurationMs?: number;
  shift?: Shift | null;
}
export type ManyRollupsWithTags = {
  [key: string]: RollupWithTagsAndCounts;
};
export type ManyRollupsWithTagsAndRange = {
  [key: string]: RollupWithTagsAndCountsAndRange;
};
export type ManyRollupsWithTagsArrays = {
  [key: string]: RollupWithTagsAndCountsAndRange[];
};

export enum TrendChartBinSizesEnum {
  hours = "hours",
  shifts = "shifts",
  days = "days",
  weeks = "weeks",
}

export enum TrendsRangeEnum {
  day = "day",
  week = "week",
  month = "month",
  ninetyDays = "ninetyDays",
}

export type TrendsPageFilterStateType = {
  group: string;
  process: Process;
  shiftName: string;
  tag: string;
  pnlCategory: PnlCategoriesEnum | null;
};

export type TagDetails = {
  name: string;
  pnlCategory: PnlCategoriesEnum;
};

export type PerformanceMetricsType = {
  uptimePercent: number;
  availPercent: number;
  teepPercent: number;
};
export type ManyPerformanceMetricsType = {
  [id: string]: PerformanceMetricsType;
};
export type ManyPerformanceMetricsTrendBarsArraysType = {
  [id: string]: {
    uptimePercentTrendBarArray: number[];
    availPercentTrendBarArray: number[];
    teepPercentTrendBarArray: number[];
  };
};

export type DefaultMachineDetailsType = {
  primaryPort: number; // port used by machine
  staticRate: number; // comes from Default sku (used as backup)
  machineCommonSkuScale: number; // comes from Default sku (used as backup)
  oeeTarget?: number;
  weekDayShiftCountTarget?: number;
  weekendShiftCountTarget?: number;
};

export type TimeStampedDataDumpType = {
  ge: Date;
  lt: Date;
  dataDump: DataDumpType[] | null;
};

export type DateAndDataDumpType = {
  ge?: Date;
  lt?: Date;
  date: string;
  shift?: number;
  dataDump: DataDumpType[];
};

export type DateAndShiftStringDataDumpType = {
  [dateAndShiftString: string]: DataDumpType[];
};

export type DataDumpType = {
  ckey: string;
  machemetryId: string;
  processName: string;
  processGroup: string | null;

  ge: string;
  lt: string;
  asOf: string;

  pnlCategory: string | null;
  tagName: string | null;
  tagTs: string | number | null;
  tagPath: string[];
  tagId: string;
  teamSize: number | null;

  sku: string | null;
  skuName: string | null;
  skuScale: number | null;

  targetRate: number | null;
  rateDenominator: number | null;
  actualRate: number | null;

  output: number;
  netOutput: number;
  grossOutput: number | null;
  rejectOutput: number | null;
  totalPotentialOutput: number | null;

  ticks: number;
  netTicks: number;
  grossTicks: number;
  rejectTicks: number;
  tickDeltaIqm: number | string | null;
  ticksPort1: number;
  ticksPort2: number;
  ticksPort3: number;
  ticksPort4: number;

  operatorId: string | null;

  shift: string | null;
  shiftStart: string | null;
  shiftEnd: string | null;

  batchQuantity: number | null;
};

export type PnlTagLoss = {
  category: string;
  downtimeLossMs: number;
  numberOfEvents: number;
  downtimeDependentSpeedLossMs: number;
  downtimePlusDependentSpeedLossMs: number;
  name: string;
  path: string[];
};

type MinorStopLoss = {
  numberOfEvents: number;
  averageDurationMs: number;
};

type PnlProducingLoss = {
  category: string;
  outputLoss: number;
  numberOfEvents: number;
  averageDurationMs: number;
  minorStops: MinorStopLoss;
};

export type PnlKnownLoss = {
  category: string;
  downtimeLossMs: number;
  downtimeDependentSpeedLossMs: number;
  downtimePlusDependentSpeedLossMs: number;
  outputLoss: number;
  numberOfEvents: number;
  averageDurationMs: number;
  tags: PnlTagLoss[];
};
export type PnlUnknownLoss = {
  category: string;
  downtimeLossMs: number;
  downtimeDependentSpeedLossMs: number;
  downtimePlusDependentSpeedLossMs: number;
  downtimeDependentSlowdownLossMs: number;
  downtimeDependentMinorStopLossMs: number;
  independentSpeedLossMs: number;
  independentSlowdownLossMs: number;
  independentMinorStopLossMs: number;
  speedLossMs: number;
  slowdownLossMs: number;
  minorStopLossMs: number;
  outputLoss: number;
  numberOfEvents: number;
  averageDurationMs: number;
};
export type PnlUncollectedLoss = {
  category: string;
  totalDurationMs: number;
  numberOfEvents: number;
  averageDurationMs: number;
};
type PnlQualityLoss = {
  category: string;
  qualityLossMs: number;
  qualityLossOutput: number;
  scrapLossMs: number;
  scrapLossOutput: number;
  reworkLossMs: number;
  reworkLossOutput: number;
};

export type PnlLosses = {
  range: { ge: string; lt: string };
  asOf: string;
  producing: PnlProducingLoss;
  planned: PnlKnownLoss;
  waiting: PnlKnownLoss;
  unplanned: PnlKnownLoss;
  unknown: PnlUnknownLoss;
  notScheduled: PnlKnownLoss;
  uncollected: PnlUncollectedLoss;
  quality: PnlQualityLoss;
};

export type DataDumpAggregateReportsType = {
  ge: string;
  lt: string;
  asOf: string;
  pnlLosses: PnlLosses;
  machemetryIds: string[];
  processNames: string[];
  calendarTime: number;
  collectableTime: number;
  uncollectableTime: number;
  collectedTime: number;
  uncollectedTime: number;
  producingTime: number;
  productionTime: number;
  downTime: number;
  plannedTime: number;
  unplannedTime: number;
  waitingTime: number;
  unknownTime: number;
  notScheduledTime: number;
  speedLossTime: number;
  speedGainTime: number;
  runningTime: number;
  minorStopTime: number;
  slowdownLossTime: number;
  slowdownLossTimeReal: number;
  speedupGainTime: number;
  rejectLossTime: number;
  rejectLossTimeNonProducing: number;
  effectiveRunningTime: number;
  effectiveProducingTime: number;
  effectiveProducingTimeTopDown: number;
  effectiveProducingTimeBottomUp: number;
  netOutput: number;
  grossOutput: number;
  rejectOutput: number;
  totalPotentialOutput: number;
  grossOutputNonProducing: number;
  collectedPotentialOutput: number;
  productionPotentialOutput: number;
  producingPotentialOutput: number;
  outputLossSum: number;
  qualityPercent: number | null;
  numberOfStops: number;
  numberOfMinorStops: number;
  oeePercent: number | null;
  oeePercentTopDown: number | null;
  oeePercentBottomUp: number | null;
  teepPercent: number;
  availabilityPercent: number | null;
  uptimePercent: number | null;
  performancePercent: number | null;
  plannedEfficiencyPercent: number | null;
  ticksPort1: number;
  ticksPort2: number;
  ticksPort3: number;
  ticksPort4: number;
  meanTimeToRecovery: number | null;
  avgCycleTime: number | null;
  meanTimeBetweenFailures: number | null;
  lastSegment: DataDumpRow | null;
  __typename?: string;
};

export type Allocation = {
  ge: string;
  lt: string;
  machemetryId: string;
  category: string;
  tagName: string;
  numberOfMinutes: string;
  isChangeover: boolean;
};

export type ChangeoverSummary = {
  range: { ge: string; lt: string };
  tagName: string;
  changeoverDuration: number;
  lossTagName: string;
  lossDuration: number;
  isOngoingChangeover: string;
};

export type PciChartDataType = {
  date: string;
  shift?: number;
  netOutput: number;
  targetOutput: number;
  existingNetOutput?: number;
  waiting: number;
  unplanned: number;
  unknown: number;
  producing: number;
  notScheduled: number;
  planned: number;
  oeePercent: number;
  waitingTime: number;
  unplannedTime: number;
  unknownTime: number;
};

export type TagTimeDistributionType = {
  date: string;
  shift?: number;
};
export type PciReportProps = {
  dateString: string;
  subHeader: string;
  currentWeekNumber: number;
  completedYearlyData: PciChartDataType[] | null;
  shiftsDataArray: PciChartDataType[] | null;
  tagTimeDistributionByShift: TagTimeDistributionType[] | null;
  filteredtotalTimePerTagLastWeekData: pnlCategoriesData[] | null;
  lastWeeksWeeklyResults: DateAndDataDumpType | null;
  machineName: string;
  totalTimePerTagTargetWeekAggregateData: pnlCategoriesData[] | null;
  oeeTarget?: number;
};

export type DataDumpGetterMultiReturn = {
  ge: string;
  lt: string;
  queryData: DataDumpType[] | DataDumpAggregateReportsType;
};

export type DataDumpGetterReturn = {
  queryData: DataDumpType[] | DataDumpAggregateReportsType;
};

export type CustomerJsonThresholds = {
  Availability?: number;
  "Effective Time"?: number;
  OEE?: number;
  Performance?: number;
  Planned?: number;
  Quality?: number;
  RavenCoverage?: number;
  Scheduled?: number;
  TEEP?: number;
  Tagging?: number;
  Uptime?: number;
};

export type CustomerJsonType = {
  timeZone: string;
  thresholds: CustomerJsonThresholds;
  machineGroups?: {
    flight: boolean;
    flightId: string;
    machineGroupId: string;
    name: string;
    shortName: string;
    subMachineGroups: string[];
  };
  operatorGroups?: {
    [operatorId: string]: {
      name: string;
      operatorGroupId: string;
      avatar_letters?: string;
      avatar_letters_bg?: string;
      avatar_letters_fg?: string;
    };
  };
  thresholdsByProcessGroup?: {
    [processGroupName: string]: Partial<CustomerJsonThresholds>;
  };
};
export type ShiftRotation = {
  start: string;
  end: string;
  midgardShiftId: number;
};
export type WorkScheduleShift = {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
};
export type WorkScheduleDay = {
  name: string;
  ordinal: number;
  shifts: WorkScheduleShift[];
  shiftsCount: number;
};
export type WorkScheduleConfig = {
  id: string;
  name: string;
  timeZone: string;
  operational: boolean;
  processesCount: number;
  processes: ProcessWithTaps[];
  startDate: string;
  endDate: string;
  rotationLength: number;
  rotation: WorkScheduleDay[];
};

export type ReportPageProps<DataType> = {
  headerTitle: string;
  dateString: string;
  subHeader: string;
  data?: DataType;
  width?: number;
  title: string;
};

export type TargetConstantType = {
  year: number;
  week: number;
  oeeTarget: number;
  downtimePerHourTarget: number;
  countPerHourTarget: number;
  opeqTarget: number | null;
};

export type GenericRechartsProps = {
  title?: string;
  data?: any[] | null;
  xLabel?: string;
  xKey?: string;
  yLabel: string;
  yKey?: string;
  vertical?: boolean;
  bars?: any[];
  width: number;
  height: number;
  useGrid?: boolean;
  referenceAreas?: any[];
  referencelines?: any[];
  targetLines?: any[];
  targetPoints?: any;
  legend?: any;
  customBars?: any;
  tooltipContent?: any;
  tooltipLabelFormatter?: any;
  yAxisMaxCharsPerLine?: number;
  yAxisDomain?: number[];
  xAxisDomain?: number[];
  xAxisCustomProps?: { xKey: string; customTicks: any };
};

export type ManualDataAdjustmentType = {
  ge: number;
  lt: number;
  count: number;
  time: number;
};

export type shiftDayWeekTimeRanges = {
  shifts: SimplifiedShiftSegment[][]; // TODO: rename to daysOfShifts
  days: TimeRangeNN[];
  week: TimeRangeNN;
};

export type CategoryPropsType = {
  category: string;
  name?: string;
  title?: string;
  shouldWeightBars?: boolean;
  shouldHideValue?: boolean;
  hidePercentBar?: boolean;
  specialSubTags?: SpecialSubTagType[];
  shouldFillWithLines?: boolean;
  hideBorder?: boolean;
  hideNbEvents?: boolean;
  hideLostOutput?: boolean;
};

export type SpecialSubTagType = {
  title: string;
  name: string;
  time: number;
  nbEvents?: number;
  lostOutput?: number;
  color?: string;
  shouldFillWithLines?: boolean;
  hideBorder?: boolean;
  hidePercentBar?: boolean;
  shouldWeightBars?: boolean;
};

export type FlightDashboardLinkType = {
  machemetryId: string;
  dashboardLink: string | null;
};

export type PciChartProps = {
  data: PciChartDataType[] | null;
  width: number;
  height: number;
  oeeTarget?: number;
  showPlanned?: boolean;
};

export type pnlCategoriesData = {
  category: PnlCategoriesEnum;
  tag: string;
  totalTime: number;
  timeAndOccurences?: string;
};
